// src/components/about/EngineeringApproach.tsx
import { useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, useReducedMotion } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';

import { aboutConfig, type AboutApproachStage } from '../../config/aboutConfig';
import {
  ApproachRoot,
  ApproachContainer,
  SectionHeader,
  Eyebrow,
  EyebrowMark,
  Heading,
  Lede,
  DesktopFlow,
  FlowList,
  FlowNode,
  FlowConnector,
  NodeDot,
  NodeLabel,
  StagePanel,
  StageBody,
  StageHead,
  StageIndex,
  StageTitle,
  StageIntent,
  StageDetail,
  StageSplit,
  StageCol,
  MetaLabel,
  PatternList,
  Pattern,
  EvidenceText,
  ApproachNote,
  MobileStepper,
  StepList,
  StepItem,
  StepButton,
  StepIndex,
  StepText,
  StepTitle,
  StepHint,
  StepChevron,
  StepRegion,
  StepBody,
} from './EngineeringApproach.styles';
import { markerIn, motionSet, DURATION, EASE_OUT, STAGGER } from './motion';

const headingId = 'about-approach-heading';

// Declared at module scope on purpose. As a component-local declaration this
// would be a new component type on every render, so React would unmount and
// rebuild the whole stage panel — including restarting its enter animation —
// on each render, not just on each stage change.
const StageContent = ({ item }: { item: AboutApproachStage }) => (
  <>
    <StageHead>
      <StageIndex>{item.index}</StageIndex>
      <StageTitle>{item.title}</StageTitle>
    </StageHead>

    <StageIntent>{item.intent}</StageIntent>
    <StageDetail>{item.detail}</StageDetail>

    <StageSplit>
      <StageCol>
        <MetaLabel>Patterns &amp; practices</MetaLabel>
        <PatternList>
          {item.patterns.map((pattern) => (
            <Pattern key={pattern}>{pattern}</Pattern>
          ))}
        </PatternList>
      </StageCol>

      <StageCol>
        <MetaLabel>Where it showed up</MetaLabel>
        <EvidenceText>{item.evidence}</EvidenceText>
      </StageCol>
    </StageSplit>
  </>
);

const EngineeringApproach = () => {
  const { eyebrow, heading, lede, note, stages } = aboutConfig.approach;
  const reduceMotion = useReducedMotion() ?? false;

  const [activeId, setActiveId] = useState(stages[0].id);
  const [openId, setOpenId] = useState(stages[0].id);
  const nodeRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const activeIndex = stages.findIndex((item) => item.id === activeId);
  const active = stages[activeIndex] ?? stages[0];
  const motion = motionSet(reduceMotion);

  const focusStage = (index: number) => {
    const bounded = (index + stages.length) % stages.length;
    setActiveId(stages[bounded].id);
    nodeRefs.current[bounded]?.focus();
  };

  const handleStageKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        focusStage(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        focusStage(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusStage(0);
        break;
      case 'End':
        event.preventDefault();
        focusStage(stages.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <ApproachRoot aria-labelledby={headingId}>
      <ApproachContainer>
        <SectionHeader
          variants={motion.reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <Eyebrow>
            <EyebrowMark aria-hidden="true" />
            {eyebrow}
          </Eyebrow>
          <Heading id={headingId}>{heading}</Heading>
          <Lede>{lede}</Lede>
        </SectionHeader>

        {/* ---------- Desktop: horizontal pipeline ---------- */}
        <DesktopFlow>
          <FlowList role="tablist" aria-orientation="horizontal" aria-label="Engineering workflow stages">
            {stages.map((item, index) => {
              const isActive = item.id === active.id;
              const isVisited = index <= activeIndex;

              return (
                <FlowNode
                  key={item.id}
                  ref={(node) => {
                    nodeRefs.current[index] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`approach-tab-${item.id}`}
                  aria-selected={isActive}
                  aria-controls={`approach-panel-${item.id}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveId(item.id)}
                  onKeyDown={(event) => handleStageKeyDown(event, index)}
                >
                  {index > 0 && (
                    <FlowConnector
                      aria-hidden="true"
                      initial={reduceMotion ? { opacity: 0 } : { scaleX: 0 }}
                      whileInView={reduceMotion ? { opacity: 1 } : { scaleX: 1 }}
                      viewport={{ once: true, amount: 0.6 }}
                      transition={{
                        duration: reduceMotion ? DURATION.microReduced : DURATION.reveal,
                        // The connector spans node[index - 1] to node[index], so it
                        // draws from the moment the node it grows out of lands.
                        delay: motion.offset(index - 1, STAGGER.list),
                        ease: EASE_OUT,
                      }}
                    />
                  )}

                  <NodeDot
                    className="approach-node-dot"
                    aria-hidden="true"
                    variants={markerIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.6 }}
                    transition={{
                      duration: reduceMotion ? DURATION.microReduced : DURATION.reveal,
                      delay: motion.offset(index, STAGGER.list),
                      ease: EASE_OUT,
                    }}
                    $active={isActive}
                    $visited={isVisited}
                  >
                    {item.index}
                  </NodeDot>

                  <NodeLabel $active={isActive}>{item.short}</NodeLabel>
                </FlowNode>
              );
            })}
          </FlowList>
        </DesktopFlow>

        <StagePanel
          role="tabpanel"
          id={`approach-panel-${active.id}`}
          aria-labelledby={`approach-tab-${active.id}`}
          tabIndex={0}
        >
          <AnimatePresence mode="wait" initial={false}>
            <StageBody
              key={active.id}
              variants={motion.panel}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <StageContent item={active} />
            </StageBody>
          </AnimatePresence>
        </StagePanel>

        {/* ---------- Mobile: single-open stepper ---------- */}
        <MobileStepper>
          <StepList>
            {stages.map((item, index) => {
              const isOpen = item.id === openId;

              return (
                <StepItem key={item.id}>
                  <StepButton
                    type="button"
                    $active={isOpen}
                    $isLast={index === stages.length - 1}
                    aria-expanded={isOpen}
                    aria-controls={`approach-step-${item.id}`}
                    onClick={() => setOpenId(isOpen ? '' : item.id)}
                  >
                    <StepIndex $active={isOpen} aria-hidden="true">
                      {item.index}
                    </StepIndex>

                    <StepText>
                      <StepTitle $active={isOpen}>{item.title}</StepTitle>
                      <StepHint>{item.intent}</StepHint>
                    </StepText>

                    <StepChevron
                      $active={isOpen}
                      aria-hidden="true"
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: reduceMotion ? 0 : DURATION.micro, ease: EASE_OUT }}
                    >
                      <FaChevronDown />
                    </StepChevron>
                  </StepButton>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <StepRegion
                        id={`approach-step-${item.id}`}
                        initial={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        animate={reduceMotion ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
                        exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        transition={{
                          duration: motion.morph,
                          ease: EASE_OUT,
                        }}
                      >
                        <StepBody>
                          <StageDetail>{item.detail}</StageDetail>

                          <StageCol>
                            <MetaLabel>Patterns &amp; practices</MetaLabel>
                            <PatternList>
                              {item.patterns.map((pattern) => (
                                <Pattern key={pattern}>{pattern}</Pattern>
                              ))}
                            </PatternList>
                          </StageCol>

                          <StageCol>
                            <MetaLabel>Where it showed up</MetaLabel>
                            <EvidenceText>{item.evidence}</EvidenceText>
                          </StageCol>
                        </StepBody>
                      </StepRegion>
                    )}
                  </AnimatePresence>
                </StepItem>
              );
            })}
          </StepList>
        </MobileStepper>

        <ApproachNote>
          <strong>Applied selectively</strong> — {note}
        </ApproachNote>
      </ApproachContainer>
    </ApproachRoot>
  );
};

export default EngineeringApproach;
